import React, { memo, useEffect, useState } from 'react';
import cn from 'classnames';
import { allUpItemFragment } from '@src/utils/type-utils';
import { ContentBlockFragment } from '@graphqlTypes/sitecore';
import FeatureStar from '@public/images/kol/Star.svg';
import { useTranslation } from 'next-i18next';
import {
    CMSData,
    getRaceNum,
    isCOMBI,
    isWinPLA,
    oddsDropBlock,
    oddsTypes,
    RStatus,
    SCRRStatus,
    SCRStatus,
} from '@src/utils/kolBetpickUtils';
import BetPickTable from './BetPickTable';
import WinPlace from './WinPlace';
import { AppLink } from '@components/common/AppLink';
import { GetOddsDataQuery } from '@src/hooks/GetOddsDataQuery';
import { format } from 'date-fns';

type Props = {
    allUpItem: allUpItemFragment;
    jsBridge: any;
    raceRunner: any[];
    raceDay: number;
    venueCode: string;
    contentBlock?: ContentBlockFragment | null;
};

/** 用于betpick-ALL UP -swipe beline 组件 */
const AllUpSwipeItem = ({ allUpItem, raceRunner, raceDay, venueCode }: Props) => {
    /** Style logic
 * For Tablet/Desktop : The all-up inner box width in tablet (vertical or horizontal) and desktop
If there are 2 races → display them dynamically to occupy the full width of the screen.
If there are 3 races → fix each race at 300px width.
For Mobile : The outter box should be dynamic is width and the inner box width should be fixed width: 
273 px for QQP
230px for other Bet types
 */
    const { i18n, t } = useTranslation('kol');
    const lang = i18n.language.toLowerCase() === 'en-us';

    // race title
    const getPath = getRaceNum(allUpItem?.path);
    const url = `/betpick/${getPath?.betline}/${getPath?.race}/`;

    const isFeatured = allUpItem?.isFeatured?.boolValue;
    /** 场次 */
    const raceNo = allUpItem?.parent?.raceNo?.numberValue || 0;

    // race
    const displayOdds = true;
    const betTypeName = allUpItem?.betPickType?.targetItem?.name ?? '';

    /** 常规数据 */
    const [generalData, setGeneralData] = useState<any[]>([]);
    /** win/PLACE数据 */
    const [winPLAData, setWinPLAData] = useState<any[]>([]);
    /** 组合数据 */
    const [combinedData, setCombinedData] = useState<any>();
    /** REFUND 状态数据 是否停售or結束 */
    const [refundData, setRefundData] = useState({
        WIN: false,
        PLA: false,
        QIN: false,
        QPL: false,
        TRI: false,
    });

    /** 数据处理 */
    const processData = (kolOddsList: any[] = [], lang = true, t: any) => {
        // console.log('kolOddsList=================', kolOddsList);
        /** 检查 REFUND 状态 */
        const checkRefundStatus = (oddsType: string) => {
            return kolOddsList.some(
                (item) => item.oddsType === oddsType && item.status.includes('REFUND')
            );
        };

        // 更新 REFUND 状态
        const updatedRefundData = oddsTypes.reduce(
            (acc, type) => {
                acc[type as keyof typeof acc] = checkRefundStatus(type);
                return acc;
            },
            {
                WIN: false,
                PLA: false,
                QIN: false,
                QPL: false,
                TRI: false,
            } as { WIN: boolean; PLA: boolean; QIN: boolean; QPL: boolean; TRI: boolean }
        );
        setRefundData(updatedRefundData);

        const data = CMSData(allUpItem, raceRunner, kolOddsList, lang, t) ?? [];

        if (isCOMBI.includes(betTypeName)) {
            // console.log('组合data---', data);
            setCombinedData(data);
        } else if (betTypeName === isWinPLA) {
            // console.log('winPLA===data---', data);
            setWinPLAData(data);
        } else {
            // console.log('常规data---', data);
            setGeneralData(data);
        }
    };

    /** odds 数据请求 */
    const getOddsData = async () => {
        const getRaceDay = format(new Date(raceDay), 'yyyy-MM-dd');

        try {
            const oddsResult = await GetOddsDataQuery(getRaceDay, venueCode, raceNo);
            const data = oddsResult[0]?.pmPools ?? [];
            processData(data, lang, t);
        } catch (error) {
            console.error('Error fetching odds data:', error);
        }
    };

    useEffect(() => {
        getOddsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceNo, raceRunner]);

    /** 每30s请求一次 */
    useEffect(() => {
        const interval = setInterval(async () => {
            await getOddsData();
        }, 300000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceNo]);

    /** 表格内容 */
    const tableBlock = () => {
        if (isCOMBI.includes(betTypeName)) {
            return (
                <BetPickTable
                    combinedData={combinedData}
                    betTypeName={betTypeName}
                    displayOdds={displayOdds}
                    refundData={refundData}
                />
            );
        }

        if (betTypeName === isWinPLA) {
            return (
                <WinPlace
                    winPLAData={winPLAData}
                    displayOdds={displayOdds}
                    refundData={refundData}
                />
            );
        }

        return (
            <div className='w-full'>
                <table className='w-full border-collapse text-[14px]'>
                    <thead>
                        <tr
                            style={{
                                borderBottom: `1px solid #E0E0E0`,
                                transform: 'translateY(-2px)',
                            }}
                        >
                            <th className='text-left text-[11px] font-normal'>{t('horse')}</th>
                            {displayOdds && (
                                <th className='text-center text-[11px] font-normal w-[36px]'>
                                    {betTypeName === 'PLA' ? t('Pla') : t('Win')}
                                </th>
                            )}
                        </tr>
                        <tr className='h-[1px]' />
                    </thead>

                    <tbody>
                        <tr className='h-1' />
                        {generalData.map((i, index) => {
                            const isSCR = SCRStatus.includes(i.status) || i.oddsValue === 'SCR';
                            const isR = RStatus.includes(i.status);
                            const isSCRR = SCRRStatus.includes(i.status);
                            const noBG =
                                betTypeName === 'PLA'
                                    ? i.oddsValue === 'SCR' ||
                                      i.oddsValue === '---' ||
                                      refundData.WIN
                                    : i.oddsValue === 'SCR' ||
                                      i.oddsValue === '---' ||
                                      refundData.PLA;

                            return (
                                <tr
                                    key={index}
                                    className='text-[13px] font-bold text-black pt-[5px] h-[20px]'
                                >
                                    <td>
                                        {`${i.no}. ${isR || isSCRR ? t('betpick.r') : ''}${
                                            lang ? i.name_en : i.name_ch
                                        }`}
                                        {(isSCR || isSCRR) && ` (${t('betpick.scratched')})`}
                                    </td>
                                    {displayOdds && (
                                        <td className={cn('text-center text-[#333]')}>
                                            <div
                                                className={`${
                                                    !noBG &&
                                                    oddsDropBlock(i.hotFavourite, i.oddsDropValue)
                                                }`}
                                            >
                                                {betTypeName === 'PLA'
                                                    ? refundData.PLA
                                                        ? '---'
                                                        : isSCR || isSCRR
                                                        ? `${t('betpick.scratched')}`
                                                        : i.oddsValue
                                                    : refundData.WIN
                                                    ? '---'
                                                    : isSCR || isSCRR
                                                    ? `${t('betpick.scratched')}`
                                                    : i.oddsValue}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div
            style={{ border: '1px solid #E0E0E0' }}
            className={cn(
                'relative bg-white rounded-[4px] min-h-[236px] w-full h-full overflow-clip flex flex-col p-3'
            )}
        >
            <div className='flex justify-center flex-col pb-2'>
                <div className='leading-[18px] font-bold'>
                    <AppLink href={url}>
                        {raceNo > 0 && raceNo <= 15 && t(`race${raceNo}`)} -{` `}
                        {allUpItem?.betPickType?.targetItem?.betTypeName?.value}
                    </AppLink>
                </div>
            </div>
            <div className='flex-1'>{tableBlock()}</div>
            {isFeatured && <FeatureStar className='absolute -bottom-1 -right-1'></FeatureStar>}
        </div>
    );
};

export default memo(AllUpSwipeItem);
